<template>
  <div
    :class="[
      'row', 'row--gutter-8',
      $style.keyboard,
      'flex',
      theme && $style[`theme--${theme}`],
    ]"
  >
    <div
      v-for="(key, index) in keys"
      :key="index"
      :class="['col', 'col-4', $style.keyboardKeyWrap]"
      @click="selectKey(key)"
      :data-testid="`b-keyboard-btn-value-${key}`"
    >
      <div
        :class="[$style.keyboardKey]"
      >
        <template v-if="isBackspace(key)">
          <b-icon-legacy
            :class="[$style.keyboardBackspace]"
            url="backspace.png"
            height="25"
          />
        </template>
        <template v-else-if="!(isDot(key) && !showDot)">
          {{ key }}
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { slice, concat, isNil } from 'ramda'
import { useVModel } from '@vueuse/core'

const KEY = {
  BACKSPACE: 'backspace',
  DOT: '.',
}

export default {
  name: 'b-keyboard',
  emits: ['update:modelValue'],

  props: {
    modelValue: {
      type: [String, Number],
    },
    min: {
      type: [String, Number],
      default: null,
    },
    max: {
      type: [String, Number],
      default: null,
    },
    theme: {
      type: String,
      required: false,
    },
    showDot: {
      type: Boolean,
      default: true,
    },
  },

  setup (props, { emit }) {
    const model = useVModel(props, 'modelValue', emit)

    return {
      emit,
      model,
    }
  },

  data () {
    return {
      keys: [
        '1', '2', '3', '4', '5', '6', '7', '8', '9', KEY.DOT, '0', KEY.BACKSPACE,
      ],
    }
  },

  methods: {
    selectKey (key) {
      if (!this.showDot && key === KEY.DOT) return

      let v = this.model.toString()
      if (v === '0' && key !== KEY.DOT) v = ''
      if (v === '0.00' && !this.isBackspace(key)) v = ''
      if (!this.isValid(v, key)) return

      if (key === KEY.BACKSPACE) v = slice(0, -1, v)
      else v = concat(v, key)
      this.emit('update:modelValue', v)
    },

    isValid (v, key) {
      if (key === KEY.DOT && v.includes(KEY.DOT)) return false
      if (
        v.includes(KEY.DOT)
        && !this.isBackspace(key)
        && (v.length - v.indexOf(KEY.DOT) - 1) >= 2 // number of decimal places
      ) return false

      const possibleValue = concat(v, key)
      if (!this.isBackspace(key) && !isNil(this.min) && parseFloat(possibleValue || 0) < parseFloat(this.min)) return false
      if (!this.isBackspace(key) && !isNil(this.max) && parseFloat(possibleValue || 0) > parseFloat(this.max)) return false

      return true
    },

    isBackspace (key) {
      return key === KEY.BACKSPACE
    },

    isDot (key) {
      return key === KEY.DOT
    },
  },
}
</script>

<style lang="scss" module>
@layer legacy-views {

.keyboard {
  .keyboardKey {
    cursor: pointer;
    height: 75px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--color-gray-14);
    border-radius: 8px;
    background: var(--color-white);
    font-size: 1.5rem;
    font-weight: 600;
    box-shadow: 0 2px 4px 0 rgba(49, 47, 51, .03);
  }
  .keyboardBackspace {
    color: var(--color-gray-14);
  }
}

.theme {
  &--narrow {
    margin: 0 !important;
    width: 316px;

    @include media-breakpoint-desktop {
      width: 400px;
    }
    .keyboardKeyWrap {
      padding: 0 !important;
    }
    .keyboardKey {
      width: 100%;
      height: 60px;
      margin-bottom: 0;
      border-radius: 0;
      @include media-breakpoint-desktop {
        height: 88px;
      }
    }
  }
}

}</style>
